import React, { useRef, useEffect, useCallback } from 'react';

const PhotoCarousel = () => {
  const scrollRef = useRef(null);
  const intervalRef = useRef(null);
  const isJumping = useRef(false);
  const cardWidthRef = useRef(null);

  // 10 photos réelles — on les triplique pour l'effet boucle infinie sans rebond
  const basePhotos = Array.from({ length: 10 }, (_, i) => `/images/caroussel/kayak-lagon-${i + 1}.webp`);
  // On crée un tableau [original, clone, clone] — le défilement vit dans le "clone du milieu"
  const photos = [...basePhotos, ...basePhotos, ...basePhotos];

  // Largeur d'un groupe = scrollWidth / 3
  const getGroupWidth = useCallback(() => {
    if (!scrollRef.current) return 0;
    return scrollRef.current.scrollWidth / 3;
  }, []);

  // Au montage, on se positionne dans le groupe du milieu pour pouvoir défiler dans les deux sens
  useEffect(() => {
    if (scrollRef.current) {
      const groupWidth = getGroupWidth();
      scrollRef.current.scrollLeft = groupWidth;
    }
  }, [getGroupWidth]);

  // Après chaque défilement fluide, on vérifie si on doit téléporter sans animation
  const handleScroll = useCallback(() => {
    if (isJumping.current || !scrollRef.current) return;
    const { scrollLeft } = scrollRef.current;
    const groupWidth = getGroupWidth();

    // Si on dépasse le dernier groupe : on téléporte au début du 2e groupe (silencieusement)
    if (scrollLeft >= groupWidth * 2) {
      isJumping.current = true;
      scrollRef.current.style.scrollBehavior = 'auto';
      scrollRef.current.scrollLeft = scrollLeft - groupWidth;
      scrollRef.current.style.scrollBehavior = '';
      requestAnimationFrame(() => { isJumping.current = false; });
    }
    // Si on remonte avant le 1er groupe : on téléporte dans le 2e groupe (silencieusement)
    else if (scrollLeft <= 0) {
      isJumping.current = true;
      scrollRef.current.style.scrollBehavior = 'auto';
      scrollRef.current.scrollLeft = scrollLeft + groupWidth;
      scrollRef.current.style.scrollBehavior = '';
      requestAnimationFrame(() => { isJumping.current = false; });
    }
  }, [getGroupWidth]);

  // Auto-scroll : avance d'une carte toutes les 3 secondes
  useEffect(() => {
    const startAutoScroll = () => {
      intervalRef.current = setInterval(() => {
        if (scrollRef.current && !isJumping.current) {
          // On cache la largeur de la carte pour éviter le Forced Reflow de Lighthouse
          if (!cardWidthRef.current) {
            cardWidthRef.current = scrollRef.current.querySelector('div')?.offsetWidth ?? 400;
          }
          scrollRef.current.scrollBy({ left: cardWidthRef.current + 24, behavior: 'smooth' });
        }
      }, 3000);
    };

    startAutoScroll();
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <section className="py-8 bg-white overflow-hidden">
      <div className="max-w-[100rem] mx-auto">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-4 sm:gap-6 overflow-x-auto pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollBehavior: 'smooth' }}
        >
          {photos.map((src, idx) => (
            <div
              key={idx}
              className="relative shrink-0 w-[85vw] sm:w-[420px] md:w-[520px] aspect-[4/3] rounded-3xl overflow-hidden shadow-md border border-slate-100"
            >
              <img
                src={src}
                alt={`Tahaa Lagoon Kayak - kayak dans le lagon de Taha'a`}
                width="520"
                height="390"
                loading={idx >= 10 && idx <= 13 ? "eager" : "lazy"}
                fetchpriority={idx >= 10 && idx <= 13 ? "high" : "auto"}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PhotoCarousel;
